import React from 'react';

const INPUT_CLASS = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500";

export default function CustomFieldRenderer({ fields = [], values = {}, onChange, editing = false, className = '' }) {
    if (!fields.length) return null;

    const handleChange = (key, value) => {
        if (onChange) {
            onChange({ ...values, [key]: value });
        }
    };

    return (
        <div className={className}>
            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Custom Fields</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                {fields.map(field => {
                    const value = values[field.key] ?? field.default_value ?? '';

                    if (!editing) {
                        // View mode
                        return (
                            <div key={field.key}>
                                <label className="block text-xs text-stone-400 mb-0.5">{field.label}</label>
                                <div className="text-sm text-stone-900">
                                    {field.field_type === 'checkbox' ? (
                                        <span className={value ? 'text-emerald-600' : 'text-stone-400'}>{value ? 'Yes' : 'No'}</span>
                                    ) : field.field_type === 'multi_select' && Array.isArray(value) ? (
                                        <div className="flex flex-wrap gap-1">
                                            {value.map(v => (
                                                <span key={v} className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{v}</span>
                                            ))}
                                        </div>
                                    ) : field.field_type === 'url' && value ? (
                                        <a href={value} target="_blank" rel="noopener noreferrer" className="text-stone-900 underline">{value}</a>
                                    ) : (
                                        <span>{value || <span className="text-stone-300">--</span>}</span>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    // Edit mode
                    return (
                        <div key={field.key}>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                                {field.label}
                                {field.required && <span className="text-red-500 ml-0.5">*</span>}
                            </label>

                            {field.field_type === 'textarea' ? (
                                <textarea
                                    value={value || ''}
                                    onChange={e => handleChange(field.key, e.target.value)}
                                    rows={2}
                                    className={INPUT_CLASS}
                                />
                            ) : field.field_type === 'select' ? (
                                <select
                                    value={value || ''}
                                    onChange={e => handleChange(field.key, e.target.value)}
                                    className={INPUT_CLASS}
                                >
                                    <option value="">Select...</option>
                                    {(field.options || []).map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            ) : field.field_type === 'multi_select' ? (
                                <div className="space-y-1">
                                    {(field.options || []).map(opt => (
                                        <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={(Array.isArray(value) ? value : []).includes(opt)}
                                                onChange={e => {
                                                    const arr = Array.isArray(value) ? [...value] : [];
                                                    if (e.target.checked) arr.push(opt);
                                                    else arr.splice(arr.indexOf(opt), 1);
                                                    handleChange(field.key, arr);
                                                }}
                                                className="rounded border-stone-300"
                                            />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            ) : field.field_type === 'checkbox' ? (
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!value}
                                        onChange={e => handleChange(field.key, e.target.checked)}
                                        className="rounded border-stone-300"
                                    />
                                    {field.label}
                                </label>
                            ) : (
                                <input
                                    type={field.field_type === 'number' ? 'number' :
                                          field.field_type === 'date' ? 'date' :
                                          field.field_type === 'email' ? 'email' :
                                          field.field_type === 'phone' ? 'tel' :
                                          field.field_type === 'url' ? 'url' : 'text'}
                                    value={value || ''}
                                    onChange={e => handleChange(field.key, field.field_type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)}
                                    className={INPUT_CLASS}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
